import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import CloseIcon from '@mui/icons-material/Close';
import type { GridColDef } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter, RowAction, SearchField } from '../../components/data-grid/RouteDataGrid';
import { fmtDateTime } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import type { RoleSummary } from '../../api/types';
import {
  useDeleteRole,
  useDisableRole,
  useMe,
  useMyPermissions,
  usePermissionsList,
  useRolePermissions,
  useRolesList,
  useSetRoleAutoClone,
} from '../../api/queries';
import { useQuery } from '@tanstack/react-query';
import { ConfirmDialog } from '../../components/dialogs/ConfirmDialog';
import { CloneRoleDialog } from './CloneRoleDialog';
import { EditRolePermissionsDialog } from './EditRolePermissionsDialog';

export default function RolesPage() {
  usePageTitle('Roles');
  const list = useRolesList();
  const perms = useMyPermissions();
  const me    = useMe();
  const has = useCallback(
    (code: string) => perms.data?.codes?.includes(code) ?? false,
    [perms.data?.codes],
  );
  const isArms = me.data?.is_arms ?? false;

  const [detailRow, setDetailRow] = useState<RoleSummary | null>(null);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [editPermsRow, setEditPermsRow] = useState<RoleSummary | null>(null);
  const [confirmDisable, setConfirmDisable] = useState<RoleSummary | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<RoleSummary | null>(null);
  const disableRole = useDisableRole(confirmDisable?.id ?? '00000000-0000-0000-0000-000000000000');
  const deleteRole  = useDeleteRole(confirmDelete?.id  ?? '00000000-0000-0000-0000-000000000000');
  const setAutoClone = useSetRoleAutoClone();

  const columns = useMemo<GridColDef<RoleSummary>[]>(() => [
    {
      field: 'displayName', headerName: 'Role', flex: 1.5,
      renderCell: (p) => (
        <Stack sx={{ py: 0.5 }}>
          <Typography variant="body2">{p.row.displayName}</Typography>
          <Typography variant="caption" color="text.secondary">{p.row.code}</Typography>
        </Stack>
      ),
    },
    { field: 'tier', headerName: 'Tier', width: 130,
      renderCell: (p) => <TierChip tier={p.row.tier} /> },
    { field: 'permissionCount', headerName: 'Permissions', type: 'number', width: 130,
      align: 'right', headerAlign: 'right' },
    { field: 'isSystem', headerName: 'System', width: 90,
      renderCell: (p) => p.row.isSystem ? <Chip size="small" label="System" /> : null },
    { field: 'isActive', headerName: 'Status', width: 110,
      renderCell: (p) => p.row.isActive
        ? <Chip size="small" color="success" label="Active" />
        : <Chip size="small" label="Disabled" /> },
    // Auto-clone column: only meaningful for operator-tier templates
    // (Tier=Operator, OperatorId=NULL). Hidden / no-op elsewhere.
    // ACES is the only caller that ever sees templates, so for non-
    // ACES the column will simply have nothing to toggle.
    { field: 'autoCloneToOperators', headerName: 'Auto-clone', width: 130,
      sortable: false,
      renderCell: (p) => {
        const isTemplate = p.row.tier === 'Operator' && p.row.operatorId === null;
        if (!isTemplate) {
          return <Typography variant="caption" color="text.disabled">—</Typography>;
        }
        return (
          <Tooltip title="Cloned into every new operator on creation. ACES toggles only.">
            <span>
              <Switch
                size="small"
                checked={p.row.autoCloneToOperators}
                disabled={!isArms || setAutoClone.isPending}
                onChange={async (e) => {
                  await setAutoClone.mutateAsync({
                    roleId: p.row.id, enabled: e.target.checked,
                  });
                }}
              />
            </span>
          </Tooltip>
        );
      } },
    { field: 'createdAt', headerName: 'Created', width: 180,
      valueFormatter: (value) => fmtDateTime(value) },
  ], [isArms, setAutoClone]);

  const searchFields = useMemo<SearchField[]>(() => [
    { kind: 'text', key: 'displayName', label: 'Role name' },
    { kind: 'text', key: 'code',        label: 'Code' },
    { kind: 'select', key: 'tier', label: 'Tier', options: [
      { value: 'Platform', label: 'Platform' },
      { value: 'Operator', label: 'Operator' },
      { value: 'Supplier', label: 'Supplier' },
    ] },
  ], []);

  const quickFilters = useMemo<QuickFilter<RoleSummary>[]>(() => [
    { label: 'All',      filter: () => true },
    { label: 'Active',   filter: (r) => r.isActive },
    { label: 'Disabled', filter: (r) => !r.isActive },
    { label: 'System',   filter: (r) => r.isSystem },
    { label: 'Custom',   filter: (r) => !r.isSystem },
  ], []);

  const exportColumns = useMemo<ExportColumn<RoleSummary>[]>(() => [
    { key: 'code',            label: 'Code' },
    { key: 'displayName',     label: 'Display Name' },
    { key: 'tier',            label: 'Tier' },
    { key: 'isSystem',        label: 'System', format: (v) => v ? 'Yes' : 'No' },
    { key: 'isActive',        label: 'Active', format: (v) => v ? 'Yes' : 'No' },
    { key: 'permissionCount', label: 'Permissions' },
    { key: 'operatorId',      label: 'Operator ID' },
    { key: 'createdAt',       label: 'Created', format: (v) => fmtDateTime(v) },
    { key: 'updatedAt',       label: 'Updated', format: (v) => fmtDateTime(v) },
  ], []);

  // #160 — every seeded role except SystemMaster lost is_system=true.
  // Edit / Disable / Delete now gate on the caller's permission alone;
  // SystemMaster is the lone row where row.isSystem stays true and
  // therefore the only place these actions are still hidden.
  const actions = useMemo<RowAction<RoleSummary>[]>(() => [
    { label: 'View permissions', onClick: (row) => setDetailRow(row) },
    {
      label: 'Edit permissions',
      visible: (row) => has('roles.edit') && !row.isSystem,
      onClick: (row) => setEditPermsRow(row),
    },
    {
      label: 'Disable',
      destructive: true,
      visible: (row) => has('roles.disable') && !row.isSystem && row.isActive,
      onClick: (row) => setConfirmDisable(row),
    },
    {
      label: 'Delete',
      destructive: true,
      visible: (row) => has('roles.delete') && !row.isSystem,
      onClick: (row) => setConfirmDelete(row),
    },
  ], [has]);

  return (
    <>
      <Stack spacing={2}>
        <Typography variant="h4">Roles</Typography>

        <RouteDataGrid<RoleSummary>
          entityName="Roles"
          queryKey={list.queryKey}
          listFn={list.listFn}
          columns={columns}
          searchFields={searchFields}
          quickFilters={quickFilters}
          exportColumns={exportColumns}
          reportTitle="ARMS — Roles"
          actions={actions}
          primaryAction={
            has('roles.create')
              ? { label: '+ Clone system role', onClick: () => setCloneOpen(true) }
              : undefined
          }
          onRowClick={(row) => setDetailRow(row)}
        />

        <CloneRoleDialog open={cloneOpen} onClose={() => setCloneOpen(false)} />

        <EditRolePermissionsDialog
          role={editPermsRow}
          onClose={() => setEditPermsRow(null)}
        />

        <ConfirmDialog
          open={confirmDisable !== null}
          title="Disable role"
          message={confirmDisable ? (
            <>Disable <strong>{confirmDisable.displayName}</strong>? It will no longer apply to any group it's assigned to. The action can be reversed by re-enabling.</>
          ) : ''}
          confirmLabel="Disable"
          destructive
          busy={disableRole.isPending}
          error={(disableRole.error as { message?: string } | null)?.message}
          onConfirm={() =>
            disableRole.mutate(undefined, { onSuccess: () => setConfirmDisable(null) })
          }
          onCancel={() => setConfirmDisable(null)}
        />

        <ConfirmDialog
          open={confirmDelete !== null}
          title="Delete role"
          message={confirmDelete ? (
            <>
              Permanently delete <strong>{confirmDelete.displayName}</strong>?
              {' '}This cannot be undone. The server refuses if any user still holds the role —
              {' '}re-assign their roles first.
            </>
          ) : ''}
          confirmLabel="Delete"
          destructive
          busy={deleteRole.isPending}
          error={(deleteRole.error as { message?: string } | null)?.message}
          onConfirm={() =>
            deleteRole.mutate(undefined, { onSuccess: () => setConfirmDelete(null) })
          }
          onCancel={() => setConfirmDelete(null)}
        />

        <RoleDetailDialog
          role={detailRow}
          open={detailRow !== null}
          onClose={() => setDetailRow(null)}
        />
      </Stack>
    </>
  );
}

function TierChip({ tier }: { tier: string }) {
  const color = tier === 'Platform' ? 'secondary'
              : tier === 'Operator' ? 'primary'
              : 'default';
  return <Chip size="small" variant="outlined" label={tier} color={color as 'secondary' | 'primary' | 'default'} />;
}

function RoleDetailDialog({
  role, open, onClose,
}: {
  role: RoleSummary | null;
  open: boolean;
  onClose: () => void;
}) {
  const rolePerms = useRolePermissions(role?.id ?? null);
  const catalogue = usePermissionsList();
  const cat = useQuery({ queryKey: catalogue.queryKey, queryFn: catalogue.listFn });

  // Build a map: code → catalogue entry. Then group rolePerms by category.
  const grouped = useMemo(() => {
    if (!rolePerms.data || !cat.data) return null;
    const byCode = new Map(cat.data.map((p) => [p.code, p]));
    const out = new Map<string, { code: string; description: string; isFloor: boolean }[]>();
    for (const rp of rolePerms.data) {
      const meta = byCode.get(rp.code);
      const category = meta?.category ?? 'Other';
      const arr = out.get(category) ?? [];
      arr.push({ code: rp.code, description: meta?.description ?? '', isFloor: rp.is_floor });
      out.set(category, arr);
    }
    for (const arr of out.values()) arr.sort((a, b) => a.code.localeCompare(b.code));
    return Array.from(out.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rolePerms.data, cat.data]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Stack>
          <Typography variant="h6">{role?.displayName ?? 'Role'}</Typography>
          {role && (
            <Typography variant="caption" color="text.secondary">
              <code>{role.code}</code> · {role.tier} {role.isSystem ? '· system' : ''}
            </Typography>
          )}
        </Stack>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {rolePerms.isLoading || cat.isLoading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={20} /><Typography>Loading permissions…</Typography>
          </Stack>
        ) : rolePerms.error ? (
          <ErrorAlert>{(rolePerms.error as { message?: string }).message}</ErrorAlert>
        ) : !grouped || grouped.length === 0 ? (
          <Typography color="text.secondary">No permissions assigned to this role.</Typography>
        ) : (
          <Stack spacing={2}>
            {grouped.map(([category, perms]) => (
              <Box key={category}>
                <Typography variant="overline" color="text.secondary">
                  {category} · {perms.length}
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.5, gap: 0.75 }}>
                  {perms.map((p) => (
                    <Chip
                      key={p.code}
                      size="small"
                      variant={p.isFloor ? 'filled' : 'outlined'}
                      label={p.code}
                      color={p.isFloor ? 'primary' : 'default'}
                      sx={{ fontFamily: 'monospace', fontSize: 11 }}
                      title={p.description}
                    />
                  ))}
                </Stack>
              </Box>
            ))}
            <Alert severity="info" variant="outlined">
              <strong>Floor</strong> (filled) permissions cannot be removed when editing or cloning this role.
            </Alert>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
